import { mdiChevronDown, mdiHelpCircleOutline, mdiNotePlusOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChatBody, ChatControls, ChatHeader, ChatHistory } from '.';
import { MessagingContext, ToastContext, buildMessageAttachableEntities } from '../../../services';
import { buildAiConversationId } from '../../../services/ai-adapter/protocol';
import { Spinner, Tooltip, Variables } from '../../base';
import { AiAssistant, ExtensionUnavailableModal, useTalkToAiTrigger } from '../../ai-assistant';
import { SidePanel } from '../side-panel';
import { isEnterAuthor } from '../util';
import { hasResolveSuccess, isClaimHaltedCategory } from './resolve-section';
import * as S from './chat-panel.styles';

export interface IChatPanelProps {
    className?: string;
    hideDone?: boolean;
    hideNote?: boolean;
    onBack?: () => void;
}

export const ChatPanel: React.FunctionComponent<IChatPanelProps> = (props: IChatPanelProps): React.ReactElement => {
    const context = useContext(MessagingContext);
    const toastContext = useContext(ToastContext);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [closingTask, setClosingTask] = useState(false);
    // Optimistic flag while ResolveSection has local EMR success before comments refresh.
    const [resolveSuccessVisible, setResolveSuccessVisible] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    // Opening the panel marks the message read, so the toggle starts at "read" and only tracks
    // what the user does here. It resets per message.
    const [markedUnread, setMarkedUnread] = useState(false);
    const [readToggling, setReadToggling] = useState(false);

    const { className, hideDone, hideNote, onBack } = props;
    const {
        isDetailLoading,
        message,
        comments,
        documents,
        account,
        onCloseAndGoToNext,
        onMarkAsClosed,
        onMarkAsRead,
        onMarkAsUnread,
        onMessageClose,
    } = context;
    // Show loading only when we don't have a message yet - allows progressive rendering
    const loading = isDetailLoading && !message;
    const isSupportApp = account?.supportGrant === true;
    const isEnterTask = isEnterAuthor(message as any);
    // Composer is available for every message; ENTER task authors start collapsed (Resolve is primary).
    const canShowComposer = !!message;

    const hasClaimLinks = (message?.links?.claims?.length ?? 0) > 0;
    // Same condition ResolveSection uses to show the Success card (sync — no post-paint lag).
    const successSectionShown = useMemo(
        () =>
            isEnterTask &&
            hasClaimLinks &&
            (resolveSuccessVisible || hasResolveSuccess(comments)),
        [isEnterTask, hasClaimLinks, resolveSuccessVisible, comments],
    );

    const attachableEntities = useMemo(() => buildMessageAttachableEntities(documents), [documents]);

    const categoryNames = useMemo(() => {
        return (message?.links?.messageCategories || [])
            .map(cat => (cat.id ? context.getCategoryName?.(cat.id) : undefined))
            .filter((name): name is string => !!name);
    }, [message?.links?.messageCategories, context.getCategoryName]);

    const canCloseTask = !isClaimHaltedCategory(categoryNames);

    // Bottom link XOR Success button: open tasks only, and never when Success owns the CTA.
    // Claim Halted tasks cannot be closed manually.
    const showCloseTaskLink =
        !loading && !!message && message.open !== false && !successSectionShown && canCloseTask;

    const {
        handleClick: handleTalkToAiClick,
        checking: isAiChecking,
        tooltipTitle: aiTooltipTitle,
        showExtensionInstructions,
        closeExtensionInstructions,
        openExtensionStore,
        retryConnection,
    } = useTalkToAiTrigger(() => setAiOpen(true));

    useEffect(() => {
        setResolveSuccessVisible(false);
        setAiOpen(false);
        setMarkedUnread(false);
        setReadToggling(false);
        // Non–ENTER task messages open the composer immediately; Enter, Inc. / Enter Support start collapsed.
        setComposerOpen(!!message && !isEnterAuthor(message as any));
    }, [message?.id]);

    const handleResolveSuccessChange = useCallback((visible: boolean) => {
        setResolveSuccessVisible(visible);
    }, []);

    const handleToggleUnread = async (): Promise<void> => {
        const id = message?.id;
        if (!id || readToggling) {
            return;
        }

        const makeUnread = !markedUnread;
        setReadToggling(true);
        try {
            await (makeUnread ? onMarkAsUnread(id) : onMarkAsRead(id));
            setMarkedUnread(makeUnread);
            toastContext.notify?.(
                makeUnread ? 'Marked unread — stays flagged in your list' : 'Marked read',
                'left',
            );
        } catch (error) {
            console.error('Failed to update read state:', error);
        } finally {
            setReadToggling(false);
        }
    };

    const handleCloseAndGoToNext = async (): Promise<void> => {
        const id = message?.id;
        if (!id || closingTask || message?.open === false || !canCloseTask) {
            return;
        }

        setClosingTask(true);
        try {
            if (onCloseAndGoToNext) {
                await onCloseAndGoToNext(id);
            } else {
                await onMarkAsClosed(id);
                await onMessageClose();
            }
        } catch (error) {
            console.error('Failed to close task and go to next:', error);
        } finally {
            setClosingTask(false);
        }
    };

    const addNoteLabel = isSupportApp && hideNote !== true ? 'Leave an internal note' : 'Leave a note';
    const showGateRow = !loading && !!message;
    const showAddNoteButton = canShowComposer && !composerOpen;
    const showComposer = !loading && canShowComposer && composerOpen;

    const readToggleButton = (
        <Tooltip
            direction="top"
            variant="dark"
            title={
                <span>
                    {markedUnread
                        ? 'Marked unread — click to mark read'
                        : 'Mark this unread so it stays flagged in your list'}
                </span>
            }>
            <S.ReadToggleButton
                type="button"
                variant="unstyled"
                unread={markedUnread}
                onClick={handleToggleUnread}
                disabled={readToggling}>
                <S.ReadDot unread={markedUnread} />
                {markedUnread ? 'Unread' : 'Read'}
            </S.ReadToggleButton>
        </Tooltip>
    );

    return (
        <SidePanel className={className}>
            <ChatHeader onBack={onBack} />
            <S.Scrollable ref={scrollRef}>
                {loading && <Spinner />}
                {!loading && (
                    <>
                        <ChatBody
                            hideNote={hideNote}
                            hideDone={hideDone}
                            onResolveSuccessChange={handleResolveSuccessChange}
                        />
                        <ChatHistory hideNote={hideNote} />
                        {showCloseTaskLink && (
                            <S.CloseTaskRow>
                                <S.CloseTaskLink
                                    type="button"
                                    variant="unstyled"
                                    onClick={handleCloseAndGoToNext}
                                    disabled={closingTask}>
                                    {closingTask ? 'Closing…' : 'Close task & go to next'}
                                </S.CloseTaskLink>
                            </S.CloseTaskRow>
                        )}
                    </>
                )}
            </S.Scrollable>
            {showGateRow && !showComposer && (
                <S.GateRow>
                    <Tooltip direction="top" variant="dark" title={<span>{aiTooltipTitle}</span>}>
                        <S.GateButton
                            type="button"
                            variant="unstyled"
                            onClick={handleTalkToAiClick}
                            disabled={isAiChecking}>
                            <Icon path={mdiHelpCircleOutline} size={0.7} color={Variables.ThemeLight.TextMild} />
                            Have questions?
                        </S.GateButton>
                    </Tooltip>
                    {readToggleButton}
                    {showAddNoteButton && (
                        <S.GateButton
                            type="button"
                            variant="unstyled"
                            onClick={() => setComposerOpen(true)}>
                            <Icon path={mdiNotePlusOutline} size={0.7} color={Variables.ThemeLight.TextMild} />
                            {addNoteLabel}
                        </S.GateButton>
                    )}
                </S.GateRow>
            )}
            {showComposer && (
                <S.ComposerShell>
                    <S.ComposerToolbar>
                        <S.CollapseComposerButton
                            type="button"
                            variant="unstyled"
                            onClick={() => setComposerOpen(false)}
                            aria-label="Hide note">
                            <Icon path={mdiChevronDown} size={0.85} color={Variables.ThemeLight.TextMild} />
                        </S.CollapseComposerButton>
                        <Tooltip direction="top" variant="dark" title={<span>{aiTooltipTitle}</span>}>
                            <S.GateButton
                                type="button"
                                variant="unstyled"
                                onClick={handleTalkToAiClick}
                                disabled={isAiChecking}>
                                <Icon
                                    path={mdiHelpCircleOutline}
                                    size={0.7}
                                    color={Variables.ThemeLight.TextMild}
                                />
                                Have questions?
                            </S.GateButton>
                        </Tooltip>
                        {readToggleButton}
                    </S.ComposerToolbar>
                    <ChatControls
                        hideNote={hideNote}
                        hideDone={hideDone}
                        note={isSupportApp && hideNote !== true}
                    />
                </S.ComposerShell>
            )}
            {message?.id && (
                <>
                    <AiAssistant
                        open={aiOpen}
                        onClose={() => setAiOpen(false)}
                        conversationId={buildAiConversationId('message', message.id)}
                        entity="Message"
                        subjectLabel="message"
                        suggestionsKey="messageHeader"
                        contextData={message}
                        attachableEntities={attachableEntities}
                        utilityKey={`message:${isSupportApp ? 'Support' : 'Provider'}`}
                    />
                    <ExtensionUnavailableModal
                        open={showExtensionInstructions}
                        onClose={closeExtensionInstructions}
                        onGoToStore={openExtensionStore}
                        onRetry={retryConnection}
                    />
                </>
            )}
        </SidePanel>
    );
};
